import { useState } from 'react';
import { colors } from '@yorukiri/ui/tokens';

type Props = {
  value: number[];
  onChange: (v: number[]) => void;
  min?: number;
  max?: number;
  placeholder?: string;
};

// Chips-style editor for a list of minute intervals (learning/relearning steps).
// Each chip shows the value with its unit formatted naturally (1m, 10m, 1h,
// 1d) and an × to remove. A trailing "+ Add step" button flips into a small
// input for entering the next step.
export function StepPills({ value, onChange, min = 1, max = 10, placeholder = 'e.g. 5' }: Props) {
  const [adding, setAdding] = useState(false);
  const [raw, setRaw] = useState('');

  const remove = (i: number) => {
    if (value.length <= 1) return;
    onChange(value.filter((_, idx) => idx !== i));
  };

  const commit = () => {
    const n = Number(raw);
    if (Number.isFinite(n) && n >= min && value.length < max) {
      onChange([...value, n]);
    }
    setRaw('');
    setAdding(false);
  };

  const canAdd = value.length < max;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 6,
        minHeight: 30,
      }}
    >
      {value.map((v, i) => (
        <span key={`${i}-${v}`} style={pillStyle}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMinutes(v)}</span>
          {value.length > 1 && (
            <button
              onClick={() => remove(i)}
              aria-label="Remove step"
              style={removeBtnStyle}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {adding ? (
        <input
          autoFocus
          type="text"
          inputMode="numeric"
          value={raw}
          placeholder={placeholder}
          onChange={(e) => setRaw(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            else if (e.key === 'Escape') {
              setRaw('');
              setAdding(false);
            }
          }}
          style={addInputStyle}
        />
      ) : (
        canAdd && (
          <button
            onClick={() => setAdding(true)}
            style={addBtnStyle}
          >
            + Add
          </button>
        )
      )}
    </div>
  );
}

// Display minutes using a natural unit. 1 → 1m, 90 → 1h 30m, 1440 → 1d.
function formatMinutes(m: number): string {
  if (m < 60) return `${m}m`;
  if (m < 60 * 24) {
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
  }
  const d = Math.floor(m / 1440);
  const rem = m % 1440;
  if (rem === 0) return `${d}d`;
  const h = Math.floor(rem / 60);
  return h === 0 ? `${d}d` : `${d}d ${h}h`;
}

const pillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  height: 26,
  padding: '0 4px 0 10px',
  borderRadius: 999,
  background: colors.accentSoft,
  color: colors.accent,
  fontSize: 12.5,
  fontWeight: 500,
  letterSpacing: '-0.005em',
};

const removeBtnStyle: React.CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: 'none',
  background: 'transparent',
  color: colors.accent,
  fontSize: 16,
  lineHeight: 1,
  padding: 0,
  cursor: 'pointer',
  opacity: 0.6,
  transition: 'opacity 0.1s, background 0.1s',
};

const addBtnStyle: React.CSSProperties = {
  height: 26,
  padding: '0 10px',
  borderRadius: 999,
  border: `1px dashed ${colors.line2}`,
  background: 'transparent',
  color: colors.ink3,
  fontSize: 12,
  fontFamily: 'inherit',
  cursor: 'pointer',
  letterSpacing: '0.02em',
};

const addInputStyle: React.CSSProperties = {
  height: 26,
  width: 72,
  padding: '0 8px',
  borderRadius: 999,
  border: `1px solid ${colors.accent}`,
  background: colors.surface,
  color: colors.ink,
  fontSize: 12.5,
  fontFamily: 'inherit',
  fontVariantNumeric: 'tabular-nums',
  outline: 'none',
};
